import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatMoney } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { RatingStars } from '@/components/marketplace/RatingStars';
import { Package, ShoppingBag, Star, Heart, TrendingUp, Eye, DollarSign, Users } from 'lucide-react';
import { VerificationBanner } from '@/components/common/VerificationBanner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';

const Stat = ({ icon: Icon, label, value, hint, testId, color = 'text-red-600' }) => (
    <div className="rounded-2xl border bg-card p-4" data-testid={testId}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className={`h-3.5 w-3.5 ${color}`} />{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
        {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
);

export default function DashboardHome() {
    const { user } = useAuth();
    const [stats, setStats] = useState({ myAds: [], favorites: [], sales: [], purchases: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const [ads, favs, sales, purch] = await Promise.all([
                api.get('/me/ads'),
                api.get('/me/favorites'),
                api.get('/me/orders/sales'),
                api.get('/me/orders/purchases'),
            ]);
            setStats({ myAds: ads.data, favorites: favs.data, sales: sales.data, purchases: purch.data });
            setLoading(false);
        })();
    }, []);

    // Build charts data
    const now = new Date();
    const last14days = [...Array(14)].map((_, i) => {
        const d = new Date(now); d.setDate(d.getDate() - (13 - i));
        const dateStr = d.toISOString().slice(0, 10);
        return {
            date: dateStr.slice(5),
            ventas: stats.sales.filter(s => s.created_at?.slice(0, 10) === dateStr).length,
            compras: stats.purchases.filter(s => s.created_at?.slice(0, 10) === dateStr).length,
        };
    });

    const revenueByDay = last14days.map(d => ({
        date: d.date,
        ingresos: stats.sales.filter(s => s.created_at?.slice(0, 10).endsWith(d.date) && ['paid', 'delivered', 'released'].includes(s.status)).reduce((sum, s) => sum + (s.item_price || 0), 0),
    }));

    const totalRevenue = stats.sales.filter(s => ['paid', 'delivered', 'released'].includes(s.status)).reduce((s, o) => s + (o.item_price || 0), 0);
    const totalViews = stats.myAds.reduce((s, a) => s + (a.views || 0), 0);
    const totalFavs = stats.myAds.reduce((s, a) => s + (a.favorites_count || 0), 0);
    const activeAds = stats.myAds.filter(a => a.status === 'published').length;

    // Ads by category pie
    const catCounts = stats.myAds.reduce((acc, a) => { acc[a.category_slug] = (acc[a.category_slug] || 0) + 1; return acc; }, {});
    const categoryData = Object.entries(catCounts).map(([name, value]) => ({ name, value }));
    const COLORS = ['#dc2626', '#0f0f0f', '#7f1d1d', '#525252', '#404040', '#171717'];

    if (loading) return <div className="text-sm text-muted-foreground">Cargando estadísticas…</div>;

    return (
        <div className="space-y-6">
            <VerificationBanner />
            <div className="rounded-2xl bg-gradient-to-br from-red-600 via-red-700 to-black p-6 text-white">
                <h1 className="text-2xl font-bold tracking-tight">Hola, @{user?.nickname}</h1>
                <p className="text-sm text-white/85 mt-1">Bienvenido a tu panel. Aquí está el resumen de tu actividad.</p>
                <div className="mt-4 flex items-center gap-2">
                    <RatingStars value={user?.rating_avg || 0} showValue />
                    <span className="text-xs text-white/80">({user?.rating_count || 0} calificaciones)</span>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat icon={Package} label="Anuncios activos" value={activeAds} hint={`${stats.myAds.length} totales`} testId="stat-ads" />
                <Stat icon={TrendingUp} label="Ventas" value={stats.sales.length} testId="stat-sales" />
                <Stat icon={DollarSign} label="Ingresos brutos" value={formatMoney(totalRevenue)} testId="stat-revenue" />
                <Stat icon={ShoppingBag} label="Compras" value={stats.purchases.length} testId="stat-purchases" />
                <Stat icon={Eye} label="Vistas totales" value={totalViews} testId="stat-views" />
                <Stat icon={Heart} label="Favoritos recibidos" value={totalFavs} testId="stat-favs-received" />
                <Stat icon={Heart} label="Mis favoritos" value={stats.favorites.length} testId="stat-favs" />
                <Stat icon={Star} label="Rating promedio" value={(user?.rating_avg || 0).toFixed(1)} testId="stat-rating" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-2xl border bg-card p-4">
                    <h3 className="font-medium mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-red-600" />Ventas vs Compras (14 días)</h3>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={last14days}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                                <XAxis dataKey="date" fontSize={10} />
                                <YAxis fontSize={10} allowDecimals={false} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="ventas" fill="#dc2626" />
                                <Bar dataKey="compras" fill="#0f0f0f" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="rounded-2xl border bg-card p-4">
                    <h3 className="font-medium mb-3 flex items-center gap-2"><DollarSign className="h-4 w-4 text-red-600" />Ingresos por día</h3>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={revenueByDay}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                                <XAxis dataKey="date" fontSize={10} />
                                <YAxis fontSize={10} />
                                <Tooltip formatter={(v) => formatMoney(v)} />
                                <Line type="monotone" dataKey="ingresos" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-2xl border bg-card p-4">
                    <h3 className="font-medium mb-3">Anuncios por categoría</h3>
                    <div className="h-56">
                        {categoryData.length === 0 ? <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sin datos aún</div> :
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                                    {categoryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>}
                    </div>
                </div>
                <div className="rounded-2xl border bg-card p-4">
                    <h3 className="font-medium mb-3">Top anuncios por vistas</h3>
                    <div className="space-y-2">
                        {stats.myAds.slice().sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5).map(a => (
                            <Link key={a.id} to={`/ad/${a.id}`} className="flex items-center justify-between rounded-lg border p-2 hover:bg-secondary transition-colors">
                                <span className="truncate max-w-[70%] text-sm font-medium">{a.title}</span>
                                <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Eye className="h-3 w-3" />{a.views || 0}</span>
                            </Link>
                        ))}
                        {stats.myAds.length === 0 && <div className="text-sm text-muted-foreground">Aún no tienes anuncios.</div>}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link to="/dashboard/ads/new" className="rounded-2xl border bg-card p-4 hover:shadow-md transition-shadow">
                    <div className="text-sm font-medium">Publicar un anuncio</div>
                    <div className="text-xs text-muted-foreground mt-1">Sube fotos, videos, variantes en minutos.</div>
                </Link>
                <Link to="/dashboard/sales" className="rounded-2xl border bg-card p-4 hover:shadow-md transition-shadow">
                    <div className="text-sm font-medium">Revisar mis ventas</div>
                    <div className="text-xs text-muted-foreground mt-1">Marca como entregado y libera pagos.</div>
                </Link>
            </div>
        </div>
    );
}
